import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './PriceList.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import { useQuery } from '@tanstack/react-query';
import { getReferencialData } from '@src/api/Referencial';
import Loader from '@src/components/_shared/UI/Loader';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import { useEffect, useState } from 'react';
import ValidateChoiceButton from '@src/components/_shared/UI/ValidateChoiceButton';

export default function PriceList() {
    const { setSection, setBlock } = useNavigationStore();
    const { registration, updateAccountField } = useRegistrationStore();

    const [partnerAccountAccessAuthorization, setPartnerAccountAccessAuthorization] = useState<boolean>(false);

    const isBourseDirectOrMeilleurTaux = (registration.partner_informations.is_partner && registration.partner_informations.brand.sitecode === 'DAS') || !registration.partner_informations.is_partner;
    const isMeilleurTaux = registration.partner_informations.brand.sitecode === 'DAS';

    const { data, isLoading } = useQuery({
        queryKey: ['combinedFeesData'],
        queryFn: async () => {
            const [brokerageData, custodyData] = await Promise.all([
                getReferencialData({ type: `account/fee/brokerage${registration.partner_informations.is_partner ? '?brand=' + registration.partner_informations.brand.sitecode : ''}` }),
                getReferencialData({ type: `account/fee/custody${registration.partner_informations.is_partner ? '?brand=' + registration.partner_informations.brand.sitecode : ''}` })
            ]);
            return { brokerageData, custodyData };
        }
    });

    const _onGoBack = () => {
        setSection(5)
        setBlock('AccountOptions')
    };

    const handleSelectBrokerage = (value: string) => {
        updateAccountField(0, 'fees', { ...(registration.accounts[0]?.fees || {}), brokerage: value });
    }

    const handleSelectCustody = (value: string) => {
        updateAccountField(0, 'fees', { ...(registration.accounts[0]?.fees || {}), custody: value });
    }

    const _onValidate = () => {
        if (registration.partner_informations.is_partner) {
            return setPartnerAccountAccessAuthorization(true);
        }
        setBlock('FundsOrigin')
    }

    const handleSelectPartnerAccountAccessAuthorization = (value: boolean) => {
        updateAccountField(0, 'accessAuthorization', value);
        setBlock('FundsOrigin')
    }

    useEffect(() => {
        // si BD ou MeilleurTaux, le tarif par defaut est le tarif 5
        if (isBourseDirectOrMeilleurTaux) {
            updateAccountField(0, 'fees', { ...(registration.accounts[0]?.fees || {}), brokerage: "TARIF5" });
        }
    }, [])

    if (isLoading) {
        return (
            <Container>
                <Loader type="SPINNER" />
            </Container>
        )
    }

    return (
        <Container>
            {partnerAccountAccessAuthorization ?
                <>
                    <BackButton onClick={() => setPartnerAccountAccessAuthorization(false)}/>
                    <BlockTitle title="Autorisation de consultation de compte" />
                    <Subtitle content="Souhaitez-vous rendre consultable par un tiers tous les comptes ouverts chez Direct Securities à ce jour et à venir, et autoriser le transfert de vos données à un agrégateur ?" />
                    <div className={styles.buttons}>
                        <ValidateChoiceButton title="Oui" selected={registration.accounts[0]?.accessAuthorization === true || isMeilleurTaux} onValidate={() => handleSelectPartnerAccountAccessAuthorization(true)} />
                        <ValidateChoiceButton title="Non" selected={registration.accounts[0]?.accessAuthorization === false} inactive={isMeilleurTaux} enableTooltip={false} onValidate={() => handleSelectPartnerAccountAccessAuthorization(false)} />
                    </div>
                </>
                :
                <>
                    <BackButton onClick={_onGoBack}/>
                    <BlockTitle title="Tarifs" />
                    <Subtitle content="Sélectionnez les tarifs que vous désirez appliquer" />
                    <div style={{width: '100%'}}>
                        {data?.brokerageData ?
                            <DropdownList
                                title="Tarif courtage"
                                width="100%"
                                options={data.brokerageData}
                                selected={registration.accounts[0]?.fees && registration.accounts[0]?.fees.brokerage}
                                disabled={isBourseDirectOrMeilleurTaux}
                                onSelect={handleSelectBrokerage} />
                            : null}
                        {(!isBourseDirectOrMeilleurTaux && registration.accounts[0]?.fees && registration.accounts[0]?.fees.brokerage) ?
                            <DropdownList
                                title="Tarif droits de garde"
                                width="100%"
                                options={data?.custodyData}
                                selected={registration.accounts[0]?.fees && registration.accounts[0]?.fees.custody}
                                onSelect={handleSelectCustody} />
                            : null}
                    </div>
                    {(registration.accounts[0]?.fees.brokerage && isBourseDirectOrMeilleurTaux) || (registration.partner_informations.is_partner && registration.accounts[0]?.fees && registration.accounts[0]?.fees.custody) ?
                        <ValidateButton title="Valider" onClick={_onValidate} />
                        : null}
                </>
            }
        </Container>
    )
}
